import { useQuery } from '@tanstack/react-query';
import type { SignalAlert } from '@contracts/signals';
import { Link } from 'react-router';
import { ExportButton } from '../components/ExportButton';
import { Empty, Panel } from '../components/Panel';
import { AlertStatusBadge, AtrBadge, DirectionChip, StrategyState, singleTarget } from '../components/SignalPanel';
import { SignalTabs } from '../components/SignalTabs';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { signalPrice } from '../lib/notify';
import { useLive } from '../store/live';

function merge(live: SignalAlert[], fetched: SignalAlert[]): SignalAlert[] {
  const byId = new Map<string, SignalAlert>();
  for (const a of fetched) byId.set(a.id, a);
  for (const a of live) byId.set(a.id, a); // live updates (outcomes) win
  return [...byId.values()].sort((x, y) => y.createdAt.localeCompare(x.createdAt));
}

export function SignalsPage() {
  const status = useLive((s) => s.signalStatus);
  const live = useLive((s) => s.alerts);
  const query = useQuery({
    queryKey: ['signal-alerts'],
    queryFn: () => api.get<{ alerts: SignalAlert[] }>('/api/v1/signals/alerts?limit=200'),
    refetchInterval: 15_000,
  });
  const alerts = merge(live, query.data?.alerts ?? []);
  const closed = alerts.filter((a) => a.status !== 'open');
  const tp = closed.filter((a) => a.status === 'tp2' || a.status === 'tp3').length;
  const sl = closed.filter((a) => a.status === 'sl').length;
  const winRate = tp + sl > 0 ? Math.round((tp / (tp + sl)) * 100) : null;

  return (
    <>
      <header className="page__header">
        <h1>สัญญาณ Stochastic</h1>
        <p className="page__lede">
          H1 Stochastic 5,3,3 crossing out of the 80/20 zone + a reversal candlestick on the H1 candle that just closed,
          using your MT5 prices. SL at that candle's wick, TP $5. One alert at a time (a new one waits until the
          previous one hits TP or SL). Alerts only: no order is placed.
        </p>
      </header>
      <SignalTabs />
      <p className="small muted">
        ตั้งค่าการแจ้งเตือน (แอป / LINE / เบราว์เซอร์) อยู่ที่หน้า <Link to="/settings">Settings</Link> ·
        ใช้ร่วมกันทั้งสองระบบ
      </p>
      <div className="stack">
        <Panel title="Current state" meta={status?.evaluatedAt ? `Checked ${dateTime(status.evaluatedAt)}` : undefined}>
          {status && status.strategies.length > 0 ? (
            <div className="grid grid--2">
              {status.strategies.map((e) => (
                <StrategyState key={e.strategy} evaluation={e} />
              ))}
            </div>
          ) : (
            <Empty title="No evaluation yet">Waiting for the next closed H1 candle.</Empty>
          )}
        </Panel>
        <Panel
          title="Alert history"
          meta={`${alerts.length} alerts · TP ${tp} · SL ${sl}${winRate !== null ? ` (${winRate}% TP)` : ''} · open ${alerts.length - closed.length}`}
        >
          <ExportButton />
          <p className="small muted">
            Every alert and its result is stored permanently; this list shows the latest ones. The Excel file contains
            the full history with conditions, reasons and whether each signal was right.
          </p>
          {query.isError && alerts.length === 0 ? (
            <Empty title="Could not load alerts">The backend is unreachable; live alerts still appear here.</Empty>
          ) : alerts.length === 0 ? (
            <Empty title={query.isLoading ? 'Loading…' : 'No alerts yet'}>
              WAIT is the normal state — every condition of a strategy must pass.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="ledger">
                <caption className="visually-hidden">Signal alerts, newest first</caption>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Signal</th>
                    <th>Strategy</th>
                    <th className="r">Entry</th>
                    <th className="r">SL</th>
                    <th className="r">TP</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={a.id}>
                      <td>{dateTime(a.createdAt)}</td>
                      <td>
                        <DirectionChip direction={a.direction} />
                      </td>
                      <td>
                        {a.strategyLabel} <AtrBadge alert={a} />
                        {a.dataSource !== 'live' ? <span className="small muted"> · {a.dataSource}</span> : null}
                      </td>
                      <td className="r tabular">{signalPrice(a.entry)}</td>
                      <td className="r tabular">{signalPrice(a.stopLoss)}</td>
                      <td className="r tabular">
                        {singleTarget(a)
                          ? signalPrice(a.tp1)
                          : [a.tp1, a.tp2, ...(a.tp3 != null ? [a.tp3] : [])].map(signalPrice).join(' / ')}
                      </td>
                      <td>
                        <AlertStatusBadge alert={a} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="disclaimer">
            Results are tracked on closed M1 bid candles (SELL against bid + spread); when TP and SL fall in the same
            minute the stop is counted first. Rule-based setups, not predictions. Not financial advice.
          </p>
        </Panel>
      </div>
    </>
  );
}
